<script lang="ts" setup>
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { useFilters, useOrderPayments } from "@/composables";
import { NON_SELECTABLE_ORDER_PAYMENT_TYPES } from "@/constants/order";
import { getEntityKey } from "@/utils/sameEntity";
import OmniTable from "@/components/omni/OmniTable.vue";
import OrderPaymentStatus from "@/components/statuses/OrderPaymentStatus.vue";
import TooltipButton from "@/components/buttons/TooltipButton.vue";
import PermissionTooltip from "@/components/tooltips/PermissionTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import AddOrderPaymentDialog from "@/views/orders/view/components/AddOrderPaymentDialog.vue";
import EditOrderPaymentDialog from "@/views/orders/view/components/EditOrderPaymentDialog.vue";
import DeleteOrderPaymentDialog from "@/views/orders/view/components/DeleteOrderPaymentDialog.vue";

import type { OrderDto, OrderPayment } from "@/api/orders/dto/order.dto";

const { t } = useI18n();
const { mdash } = useFilters();
const {
  createAddOrderPaymentDisableRules,
  createChangeOrderPaymentDisableRules,
  formatOrderPaymentRate,
  isDeductionPaymentType,
} = useOrderPayments();

const props = defineProps<{
  order: OrderDto;
}>();

const emits = defineEmits<{
  (e: "succcess-create-payment", payment: OrderPayment): void;
  (e: "success-edit-payment", payment: OrderPayment): void;
  (e: "success-delete-payment", payment: OrderPayment): void;
}>();

const isAddPaymentDialogOpen = ref(false);
const isEditPaymentDialogOpen = ref(false);
const editingPayment = ref<OrderPayment | null>(null);
const isDeletePaymentDialogOpen = ref(false);
const deletingPayment = ref<OrderPayment | null>(null);

const headers = [
  { title: t("payments.type"), key: "type", sortable: false, width: 250 },
  { title: t("payments.status"), key: "status", sortable: false, align: "center", width: 130 },
  { title: t("payments.rate"), key: "rate", sortable: false, align: "end" },
  { title: "", key: "actions", sortable: false, width: 84 },
];

const addPaymentDisableRules = computed(() => {
  return createAddOrderPaymentDisableRules(() => props.order.status);
});

const activePayments = computed<OrderPayment[]>(() => {
  return props.order.orderPayments.filter((payment) => payment.isActive && !payment.isDeleted);
});

const visiblePayments = computed<OrderPayment[]>(() => {
  return props.order.orderPayments.filter((payment) => !payment.isDeleted);
});

const rows = computed(() => {
  return [...visiblePayments.value]
    .sort((a, b) => Number(isMandatoryPayment(b)) - Number(isMandatoryPayment(a)))
    .map((payment) => {
      return {
        key: getEntityKey(payment),
        payment,
        type: payment.type ? t(`order-payments.${payment.type}`) : mdash(null),
        status: payment.status,
        rate: payment.rate === null ? mdash(null) : formatOrderPaymentRate(payment.rate),
        isActive: payment.isActive,
        isDeletable: !isMandatoryPayment(payment),
        isDeduction: isDeductionPaymentType(payment.type),
        changeDisableRules: createChangeOrderPaymentDisableRules(payment),
      };
    });
});

const totalRate = computed<string>(() => {
  return formatOrderPaymentRate(activePayments.value.reduce((total, payment) => total + (payment.rate ?? 0), 0));
});

function openEditPaymentDialog(payment: OrderPayment) {
  editingPayment.value = payment;
  isEditPaymentDialogOpen.value = true;
}

function openDeletePaymentDialog(payment: OrderPayment) {
  deletingPayment.value = payment;
  isDeletePaymentDialogOpen.value = true;
}

function isMandatoryPayment(payment: OrderPayment): boolean {
  return NON_SELECTABLE_ORDER_PAYMENT_TYPES.includes(payment.type ?? "");
}
</script>

<template>
  <v-card class="order-payments">
    <div class="d-flex align-center justify-space-between">
      <div class="order-payments__title">{{ t("payments.order-payments") }}</div>

      <permission-tooltip :permission="DispatcherPermissionValue.OrdersEdit">
        <template #activator="{ disabled: hasNoPermission }">
          <tooltip-button :rules="addPaymentDisableRules">
            <template #default="{ disabled }">
              <v-btn
                :disabled="hasNoPermission || disabled"
                class="order-payments__add text-uppercase font-weight-600 font-size-12"
                color="primary"
                data-qa="order-payments-add"
                variant="outlined"
                @click="isAddPaymentDialogOpen = true"
              >
                <template #prepend>
                  <v-icon size="16">mdi-plus</v-icon>
                </template>
                {{ t("payments.add-payment") }}
              </v-btn>
            </template>
          </tooltip-button>
        </template>

        {{ t("permission.edit-order") }}
      </permission-tooltip>
    </div>

    <omni-table
      :headers="headers"
      :hover="false"
      :items="rows"
      :items-per-page="-1"
      :server-data="false"
      class="order-payments__table mt-6"
      hide-default-footer
      item-value="key"
    >
      <template #no-data>
        <span class="text-grey-darken-1 font-size-14">{{ t("payments.no-payments") }}</span>
      </template>

      <template #[`item.type`]="{ item }">
        <span :class="{ 'text-disabled': !item.isActive }">{{ item.type }}</span>
      </template>

      <template #[`item.rate`]="{ item }">
        <span :class="{ 'text-error': item.isDeduction && item.isActive, 'text-disabled': !item.isActive }">
          {{ item.rate }}
        </span>
      </template>

      <template #[`item.status`]="{ item }">
        <order-payment-status v-if="item.status" :status="item.status" />
      </template>

      <template #[`item.actions`]="{ item }">
        <div class="d-flex align-center col-gap-3 order-payments__buttons">
          <permission-tooltip :permission="DispatcherPermissionValue.OrdersEdit">
            <template #activator="{ disabled: hasNoPermission }">
              <tooltip-button :rules="item.changeDisableRules">
                <template #default="{ disabled }">
                  <v-btn
                    :disabled="hasNoPermission || disabled || !item.isActive"
                    color="primary"
                    data-qa="order-payment-edit"
                    density="compact"
                    icon="mdi-pencil-outline"
                    size="20"
                    variant="text"
                    @click="openEditPaymentDialog(item.payment)"
                  />
                </template>
              </tooltip-button>
            </template>

            {{ t("permission.edit-order") }}
          </permission-tooltip>
          <permission-tooltip v-if="item.isDeletable" :permission="DispatcherPermissionValue.OrdersEdit">
            <template #activator="{ disabled: hasNoPermission }">
              <tooltip-button :rules="item.changeDisableRules">
                <template #default="{ disabled }">
                  <v-btn
                    :disabled="hasNoPermission || disabled || !item.isActive"
                    color="error"
                    data-qa="order-payment-delete"
                    density="compact"
                    icon="mdi-delete-outline"
                    size="20"
                    variant="text"
                    @click="openDeletePaymentDialog(item.payment)"
                  />
                </template>
              </tooltip-button>
            </template>

            {{ t("permission.edit-order") }}
          </permission-tooltip>
        </div>
      </template>
    </omni-table>

    <div class="d-flex justify-end order-payments__total">
      <span>{{ t("payments.total-invoice-rate") }}:</span>
      <span data-qa="order-payments-total">{{ totalRate }}</span>
    </div>

    <add-order-payment-dialog
      v-model="isAddPaymentDialogOpen"
      :order-id="props.order.id!"
      @success="emits('succcess-create-payment', $event)"
    />

    <edit-order-payment-dialog
      v-model="isEditPaymentDialogOpen"
      :order-id="props.order.id!"
      :order-status="props.order.status"
      :payment="editingPayment"
      @success="emits('success-edit-payment', $event)"
    />

    <delete-order-payment-dialog
      v-model="isDeletePaymentDialogOpen"
      :order-id="props.order.id!"
      :payment="deletingPayment"
      @success="emits('success-delete-payment', $event)"
    />
  </v-card>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.order-payments {
  padding: 16px;
  border-radius: 4px;
  background-color: $white;
  box-shadow: 0 4px 4px -1px rgba(0, 0, 0, 0.16);

  &__title {
    font-size: 16px;
    font-weight: 500;
    line-height: 1.24;
    color: $grey-navy;
  }

  :deep(.order-payments__table) {
    table {
      border: 1px solid $grey-lighter !important;
    }
  }

  &__total {
    gap: 12px;
    padding-right: 16px;
    font-size: 13px;
    font-weight: 600;
    color: $grey-navy;
  }
}
</style>
